/* eslint-disable no-undefined,indent,operator-linebreak */
import {
    computed,
    defineComponent,
    onMounted,
    onUnmounted,
    toRef,
    watchEffect,
} from '@vue/composition-api';
import {
    ConfFormProps,
    inheritFormItemProps,
    inheritFormProps,
    SpecificPropsType,
} from './types';
import { Col, Form, FormItem } from '@ks/ks-element-ui';
import { pick } from './utils/pick';
import { computeOption } from './utils/computed-options';
import { transformComponent } from './utils/transform-component';
import { ZFormRoot } from '@ks/z-form';
import { VNode } from 'vue';
import styles from './style.module.styl';

const ZFormItem = defineComponent({
    props: {
        ...(pick((FormItem as any).props, inheritFormItemProps) as {}),
        label: {},
        content: {},
        form: {},
        preserve: {
            type: Boolean,
            default: false,
        },
    },
    setup: (props) => {
        onUnmounted(() => {
            if (!props.preserve) {
                (props.form as any).destroy();
            }
        });
        return () => {
            const { label, content, form, ...rest } = props;
            return (
                <FormItem
                    {...{
                        props: rest,
                    }}
                >
                    {/* @ts-ignore */}
                    <span class={styles.label} slot="label">
                        {label}
                    </span>
                    {content}
                </FormItem>
            );
        };
    },
});

const ConfForm = defineComponent({
    name: 'ConfForm',
    props: {
        options: {
            type: Array,
        },
        colCount: {
            type: Number,
            default: 1,
        },
        initialValue: {
            type: Function,
        },
        inline: {
            type: Boolean,
        },
        ...pick((Form as any).props, inheritFormProps),
    } as unknown as SpecificPropsType<ConfFormProps>,
    setup: (props, { emit }) => {
        const form = new ZFormRoot(props.initialValue);
        watchEffect(
            () => {
                form.config({
                    initialValue: props.initialValue,
                });
            },
            {
                flush: 'sync',
            },
        );

        const formProps = computed(() => pick(props, inheritFormProps));
        const computedOptions = computed(() =>
            props.options
                .map((opt) => computeOption(opt, { value: form.value }))
                .map((it) => ({
                    ...it,
                    render: transformComponent(it.render),
                })),
        );

        onMounted(() => {
            form.validateRequired();
        });

        return {
            value: toRef(form, 'value'),
            reset: form.reset.bind(form),
            formProps,
            submit() {
                form.validate().then((err) => {
                    if (err.length === 0) {
                        emit('submit', form.value);
                    }
                });
            },
            computedOptions,
            canSubmit: toRef(form, 'canSubmit'),
            form,
        };
    },
    render() {
        const formContent = this.computedOptions.map((it) => {
            if (it.show === false) {
                return null;
            }
            const Render = it.render;
            let content: VNode;
            if (it.field) {
                const formItemProps = pick(it, inheritFormItemProps);
                const isArrayNode = Array.isArray(this.form.value[it.field]);
                const node = isArrayNode
                    ? this.form.arrayNode(it.field)
                    : this.form.node(it.field);
                node.config(it);
                const emit = (e: string) => (val: any) => {
                    let v = val;
                    try {
                        v = val.target.value;
                        // eslint-disable-next-line no-catch-shadow
                    } catch (e) {
                        //
                    }
                    node.value = v;
                    node.validateSelf({
                        trigger: e,
                    });
                };
                const formItemContent = (
                    <Render
                        value={node.value}
                        onBlur={emit('blur')}
                        onChange={emit('change')}
                        // nativeOnBlur={emit('blur')}
                        // nativeOnChange={emit('change')}
                        form={this}
                        node={node}
                    />
                );
                content = (
                    <ZFormItem
                        labelWidth={it.label ? undefined : '0'}
                        error={node.errorMessage}
                        preserve={false}
                        {...{
                            props: {
                                required: (it.requiredRules ?? []).length > 0,
                                ...formItemProps,
                                content: formItemContent,
                                label: it.label,
                                form: this.form.children[it.field]!,
                            },
                        }}
                    />
                );
            } else {
                content = <Render form={this} />;
            }

            if (this.inline) {
                return content;
            }

            const colProps = (typeof it.col === 'number'
                ? {
                      span: it.col,
                  }
                : it.col) ?? {
                span: 24 / this.colCount!,
            };

            return (
                <Col {...{ props: colProps }} key={it.field}>
                    {content}
                </Col>
            );
        });

        return (
            <Form
                class={styles.form}
                nativeOnSubmit={(e: Event) => e.preventDefault()}
                {...{
                    props: {
                        labelWidth: '100px',
                        showMessage: true,
                        ...this.formProps,
                    },
                }}
            >
                {formContent}
            </Form>
        );
    },
});

export default ConfForm;
